import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaptureView } from './CaptureView';
import { LetterComposeView } from './LetterComposeView';
import { ScheduleView, type MessageDraft } from './ScheduleView';
import { SealSheet } from './SealSheet';
import { formatLongDate } from '../lib/format';
import './record.css';

type Step = 'choose' | 'capture' | 'compose' | 'schedule' | 'ready';

export default function RecordScreen() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('choose');
  const [blob, setBlob] = useState<Blob | null>(null);
  const [durationMs, setDurationMs] = useState<number | undefined>(undefined);
  const [letterText, setLetterText] = useState<string | undefined>(undefined);
  const [draft, setDraft] = useState<MessageDraft | null>(null);
  const [showSeal, setShowSeal] = useState(false);

  if (step === 'choose') {
    return (
      <section className="sched">
        <p className="sched__kicker">Leave a message</p>
        <h1 className="sched__title">How do you want to say it?</h1>
        <p className="sched__muted">A recording carries your face and voice across time. A letter carries your words.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
          <div className="la-cta-wrap">
            <button className="la-btn-primary" onClick={() => setStep('capture')}>Record a video</button>
          </div>
          <button className="la-btn-secondary" onClick={() => setStep('compose')}>Write a letter</button>
        </div>
        <button className="la-btn-secondary sched__later" onClick={() => navigate('/')}>Later</button>
      </section>
    );
  }

  if (step === 'capture') {
    return (
      <CaptureView
        onExit={() => setStep('choose')}
        onUseTake={(b, ms) => { setBlob(b); setDurationMs(ms); setLetterText(undefined); setStep('schedule'); }}
      />
    );
  }

  if (step === 'compose') {
    return (
      <LetterComposeView
        initialText={letterText}
        onExit={() => setStep('choose')}
        onComposed={(text) => { setLetterText(text); setBlob(null); setStep('schedule'); }}
      />
    );
  }

  if (step === 'schedule') {
    const isLetter = letterText !== undefined;
    return (
      <ScheduleView
        blob={isLetter ? undefined : (blob ?? undefined)}
        letterText={isLetter ? letterText : undefined}
        durationMs={durationMs}
        backLabel={isLetter ? 'Edit letter' : 'Retake'}
        onBack={() => setStep(isLetter ? 'compose' : 'capture')}
        onScheduled={(d) => { setDraft(d); setStep('ready'); }}
      />
    );
  }

  // step === 'ready'
  if (draft) {
    const isLetter = draft.format === 'letter';
    const isCompound = (draft.conditions?.length ?? 0) > 1;
    const arrival = isCompound
      ? 'to arrive when the conditions you set are met'
      : draft.deliveryDate === ''
        ? 'to arrive at the moment you chose'
        : `to arrive on ${formatLongDate(draft.deliveryDate)}`;
    return (
      <section className="sched sched--ready">
        <p className="sched__kicker">Ready</p>
        <h1 className="sched__title">Your {isLetter ? 'letter' : 'message'} is ready.</h1>
        <p className="sched__ready-line">For {draft.recipientName}, {arrival}.</p>
        <p className="sched__muted">Sealing encrypts it so only they can open it — at its moment.</p>
        <div className="la-cta-wrap">
          <button className="la-btn-primary" onClick={() => setShowSeal(true)}>Seal this {isLetter ? 'letter' : 'message'}</button>
        </div>
        <button className="la-btn-secondary sched__later" onClick={() => navigate('/')}>Later</button>
        {showSeal && (
          <SealSheet
            draft={draft}
            onCancel={() => setShowSeal(false)}
            onSealed={() => navigate('/')}
          />
        )}
      </section>
    );
  }

  return null;
}
